import React, { useMemo, useState } from 'react';

import debounce from 'lodash/debounce';
import { UseControllerProps, useController } from 'react-hook-form';

import { OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { Autocomplete, Box, Chip, IconButton, TextField, Typography } from '@mui/material';

import PriceParrotService from '~/services/PriceParrotService';

import { Property } from '~/types/services/priceParrot';

import { parsePropertyNameFromTAURL } from '~/utils/priceParrot';

type LocationAutocompleteProps<T> = {
  control: UseControllerProps<T>['control'];
  name: UseControllerProps<T>['name'];
  label?: string;
  disabled?: boolean;
  rules?: UseControllerProps<T>['rules'];
  onPropertySelect?: (property: Property | null) => void;
};

export default function LocationAutocomplete<T>({
  control,
  name,
  label = 'Property',
  disabled = false,
  rules,
  onPropertySelect,
}: LocationAutocompleteProps<T>) {
  const [loading, setLoading] = useState<boolean>(false);
  const [propertySearchOptions, setPropertySearchOptions] = useState<Array<Property>>([]);
  const [inputText, setInputText] = useState('');

  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    rules,
  });

  const handleSearchProperties = useMemo(
    () =>
      debounce((searchTerm: string): void => {
        setPropertySearchOptions([]);
        if (searchTerm.length < 2) {
          return;
        }

        setLoading(true);
        PriceParrotService.searchProperty(searchTerm)
          .then(({ data }) => {
            setPropertySearchOptions(data);
          })
          .catch(console.error)
          .finally(() => setLoading(false));
      }, 400),
    [],
  );

  const fieldValue = field.value as string;
  const autocompleteValue: Array<Property | string> = fieldValue ? [fieldValue] : [];

  return (
    <Autocomplete<Property, true, false, true>
      multiple
      limitTags={1}
      value={autocompleteValue}
      inputValue={inputText}
      renderTags={(value, getTagProps) => {
        const item = value[0];
        if (!item) {
          return null;
        }
        const chipLabel = typeof item === 'string' ? parsePropertyNameFromTAURL(item) ?? item : item.name;
        return <Chip label={chipLabel} size="small" {...getTagProps({ index: 0 })} />;
      }}
      freeSolo
      loading={loading}
      options={propertySearchOptions}
      getOptionKey={(option) => (typeof option === 'string' ? option : option.url)}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
      isOptionEqualToValue={(option, value) => option.url === (typeof value === 'string' ? value : value.url)}
      filterOptions={(options) => options}
      renderOption={(props, option) => (
        <Box component="li" {...props} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap>
              {option.name}
            </Typography>
            {option.location && (
              <Typography variant="caption" color="textSecondary" noWrap>
                {option.location}
              </Typography>
            )}
          </Box>
          <IconButton
            size="small"
            href={option.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            sx={{ ml: 1, flexShrink: 0 }}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
      onInputChange={(_, newInputValue, reason) => {
        if (reason === 'input') {
          setInputText(newInputValue);
          handleSearchProperties(newInputValue);
        } else if (reason === 'clear') {
          setInputText('');
          setPropertySearchOptions([]);
          field.onChange('');
          onPropertySelect?.(null);
        } else {
          setInputText(newInputValue);
        }
      }}
      onChange={(_, newValue) => {
        const last = newValue[newValue.length - 1];
        if (last && typeof last === 'object') {
          field.onChange(last.url);
          setInputText('');
          onPropertySelect?.(last);
        } else if (newValue.length === 0) {
          field.onChange('');
          setInputText('');
          onPropertySelect?.(null);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          label={label}
          disabled={disabled}
          error={!!error}
          helperText={error?.message}
        />
      )}
    />
  );
}
